import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Send, Mail, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ContactUs = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "general",
    message: ""
  });

  const categories = [
    { value: "general", label: "General Inquiry" },
    { value: "bug", label: "Report a Bug" },
    { value: "feedback", label: "Feedback / Suggestion" },
    { value: "volunteer", label: "Volunteer Interest" },
    { value: "partnership", label: "Partnership Inquiry" },
    { value: "other", label: "Other" }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Don't send credentials for public contact form
      await axios.post(`${API}/contact`, formData, { withCredentials: false });
      setSubmitted(true);
      toast.success("Your message has been sent!");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white border-b border-border sticky top-0 z-40">
          <div className="va-blue-bar h-1"></div>
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/")}
                className="hover:bg-muted"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-8 w-auto" />
                <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
                </span>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-16 max-w-lg">
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Message Sent!
              </h2>
              <p className="text-muted-foreground mb-6">
                Thank you for reaching out. Our team will review your message and respond as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate("/")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
                <Button onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", subject: "", category: "general", message: "" }); }}>
                  Send Another Message
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="va-blue-bar h-1"></div>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="hover:bg-muted"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-8 w-auto" />
              <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Page Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
            Contact Us
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Have a question, feedback, or want to report an issue? We'd love to hear from you.
          </p>
        </div>

        {/* Contact Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Send us a message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="contact-category-select"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  name="subject"
                  placeholder="Brief description of your inquiry"
                  value={formData.subject}
                  onChange={handleChange}
                  data-testid="contact-subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message <span className="text-red-500">*</span></Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Please provide as much detail as possible..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  required
                  data-testid="contact-message-textarea"
                />
              </div>

              {/* Emergency Notice */}
              <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-foreground mb-1">
                    For Emergencies
                  </p>
                  <p className="text-muted-foreground">
                    If you or someone else is in immediate danger, please call{" "}
                    <a href="tel:911" className="text-destructive font-bold hover:underline">911</a> or the 
                    Veterans Crisis Hotline at{" "}
                    <a href="tel:988" className="text-primary font-bold hover:underline">988</a> (Press 1).
                  </p>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            You can also reach V.A.L.O.R. at{" "}
            <a href="mailto:info@valorlinkusa.org" className="text-primary hover:underline font-medium">
              info@valorlinkusa.org
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-border mt-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
                <span className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground text-center sm:text-right">
                © 2025 V.A.L.O.R.™ Veterans' Alliance for Legislative & Organizational Reform, Inc.
              </p>
            </div>
            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground text-center leading-relaxed">
                <strong>Disclaimer:</strong> VALORLink provides peer support only and does not offer medical, mental health, or emergency services.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;
